import { Header } from "./-components/header"
import { useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { CheckCircle } from "lucide-react"

export const Route = createFileRoute("/(site)/anti-cheating")({
  component: AntiCheatingDeclaration,
})

function AntiCheatingDeclaration() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>Anti-Cheating Declaration — Knowledge Facts Ladder (KFL)</h1>
          <p className="text-muted-foreground">Last updated: 03.12.2025</p>

          <p>
            To participate in the Knowledge Facts Ladder ("KFL", "the League"),
            every player must read, understand, and agree to the following
            Anti-Cheating Declaration. This declaration is designed to preserve
            competitive fairness, protect the integrity of results, and ensure
            an equal playing field for all participants.
          </p>
          <p>
            By participating in any KFL quiz, event, or game, you confirm the
            following:
          </p>

          <h2>1. Independent Play</h2>
          <p>
            I declare that I will compete independently and without assistance
            from any other person, group, or external source during all
            individual competitions.
          </p>

          <h2>2. No Use of External Materials</h2>
          <p>I will not use:</p>
          <ul>
            <li>search engines, websites, or databases</li>
            <li>notes, books, documents, or reference materials</li>
            <li>messaging tools or communication channels</li>
            <li>external devices or second screens</li>
            <li>any form of digital or physical aid during gameplay</li>
          </ul>

          <h2>3. No AI or Automation Tools</h2>
          <p>I will not use:</p>
          <ul>
            <li>AI tools (including ChatGPT, Google Gemini, Claude, etc.)</li>
            <li>automated answer systems</li>
            <li>language models</li>
            <li>browser extensions or scripts</li>
            <li>any automated mechanism that assists gameplay</li>
          </ul>

          <h2>4. No Time Manipulation</h2>
          <p>I will not attempt to:</p>
          <ul>
            <li>pause or delay timers</li>
            <li>refresh pages to gain extra time</li>
            <li>manipulate device settings or internet conditions</li>
            <li>bypass or exploit time-based mechanics in any way</li>
          </ul>

          <h2>5. Camera and Recording Compliance</h2>
          <p>I agree to follow all camera and recording rules:</p>
          <ul>
            <li>I will keep my camera on during Pairs and Teams events.</li>
            <li>I will not obstruct or manipulate recordings.</li>
            <li>
              I acknowledge that certain games may be recorded randomly or when
              cheating is suspected.
            </li>
            <li>
              I consent to recordings being used for anti-cheating purposes and
              integrity checks.
            </li>
          </ul>

          <h2>6. No Multiaccounts</h2>
          <p>
            I confirm that I will use only one account across all KFL events. I
            will not create, assist with, or benefit from multiple accounts.
          </p>

          <h2>7. No Sharing or Leaking of Question Content</h2>
          <p>I will not:</p>
          <ul>
            <li>share or repost quiz questions</li>
            <li>upload proprietary files</li>
            <li>distribute answers or question sets</li>
            <li>reveal protected content</li>
            <li>use KFL questions in other competitions</li>
            <li>post or leak material anywhere that compromises fairness</li>
          </ul>
          <p>
            Screenshots or personal score summaries may only be shared if they
            do not contain protected question content.
          </p>

          <h2>8. Reporting Misconduct</h2>
          <p>
            If I witness cheating or suspicious behavior, I will report it
            responsibly and in good faith. I will not file false accusations.
          </p>

          <h2>9. Acceptance of KFL Authority</h2>
          <p>I acknowledge that:</p>
          <ul>
            <li>
              KFL administration has the authority to determine whether cheating
              has occurred
            </li>
            <li>
              KFL may use recordings, gameplay patterns, reports, or any
              relevant data to make decisions
            </li>
            <li>
              All disciplinary decisions (including bans) are final and
              non-appealable
            </li>
            <li>
              KFL may refuse participation based on credible past misconduct,
              including in other leagues
            </li>
          </ul>

          <h2>10. Consequences</h2>
          <p>I understand that violations of this Declaration may result in:</p>
          <ul>
            <li>invalidation of scores</li>
            <li>removal from events</li>
            <li>temporary suspension</li>
            <li>permanent ban from KFL</li>
          </ul>
          <p>
            Cheating or serious misconduct may result in immediate removal
            without warning.
          </p>

          <h2>11. Declaration</h2>
          <p>By participating in KFL, I confirm that:</p>
          <div className="not-prose bg-muted/50 rounded-lg p-6 space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
              <span>
                I have read and understood this Anti-Cheating Declaration.
              </span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
              <span>I agree to fully comply with its requirements.</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
              <span>I accept all consequences for any violations.</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
              <span>
                I understand that integrity is essential to the KFL community.
              </span>
            </div>
          </div>
        </article>
      </main>
    </div>
  )
}
